/**
 * Helpers purs — tri canonique des anomalies (chunk 9.D).
 *
 * Module **pur** (zéro dépendance React Query / API / Supabase / window) —
 * extrait en 9.M.1 pour permettre des tests Node sans DOM
 * (`environment: "node"`, `include: ["src/**\/*.test.ts"]`). Sémantique
 * strictement inchangée vs l'implémentation locale précédente dans
 * `lib/queries/anomalies.ts`.
 *
 * Le module ré-exporte `AnomalySeverity` + `AnomalyRow` (types pur) pour
 * que les consommateurs (UI + tests) n'aient pas à importer le module
 * `lib/queries/anomalies.ts` (qui pull `lib/api` → `lib/supabase` →
 * `window.localStorage`, cassant les tests Node).
 *
 * Règle 9.D — tri canonique :
 *   1. résolus en bas (`resolved_at != null`)
 *   2. parmi non-résolus : sévérité décroissante
 *      `critical(0)` < `high(1)` < `warn(2)` < `info(3)` (rank ASC)
 *   3. à sévérité égale : `created_at` ASC (plus ancien d'abord)
 */

export type AnomalySeverity = "info" | "warn" | "high" | "critical";

export type AnomalyRow = {
  id: string;
  tenant_id: string;
  submission_id: string | null;
  project_id: string | null;
  type: string;
  severity: AnomalySeverity;
  message: string;
  details: Record<string, unknown> | null;
  field_keys: string[] | null;
  detected_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
  created_at: string;
};

const SEVERITY_RANK: Record<AnomalySeverity, number> = {
  critical: 0,
  high: 1,
  warn: 2,
  info: 3,
};

export function sortAnomaliesCanonical(rows: AnomalyRow[]): AnomalyRow[] {
  return [...rows].sort((a, b) => {
    const aResolved = a.resolved_at != null ? 1 : 0;
    const bResolved = b.resolved_at != null ? 1 : 0;
    if (aResolved !== bResolved) return aResolved - bResolved;
    const ar = SEVERITY_RANK[a.severity] ?? 99;
    const br = SEVERITY_RANK[b.severity] ?? 99;
    if (ar !== br) return ar - br;
    return a.created_at.localeCompare(b.created_at);
  });
}
